import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { apiService } from '../../services/ApiService';
import { TipoImovel } from '../../types/Property';
import './CorretagemModal.css';

interface CorretagemModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CorretagemModal: React.FC<CorretagemModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    tipoImovel: '',
    descricao: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'unset';
      };
    } else {
      document.body.style.overflow = 'unset';
      // Reset form when closing
      setFormData({
        nome: '',
        email: '',
        telefone: '',
        tipoImovel: '',
        descricao: ''
      });
      setSubmitStatus('idle');
    }
  }, [isOpen, onClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpar mensagem de erro quando o usuário começar a digitar
    if (submitStatus === 'error') {
      setSubmitStatus('idle');
      setErrorMessage('');
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset error message
    setErrorMessage('');
    setSubmitStatus('idle');

    // Validação de campos obrigatórios
    if (!formData.nome || !formData.email || !formData.telefone || !formData.tipoImovel || !formData.descricao) {
      setSubmitStatus('error');
      setErrorMessage('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    // Validação de email
    if (!validateEmail(formData.email)) {
      setSubmitStatus('error');
      setErrorMessage('Por favor, insira um e-mail válido.');
      return;
    }

    // Validação de nome (mínimo 2 caracteres)
    if (formData.nome.trim().length < 2) {
      setSubmitStatus('error');
      setErrorMessage('O nome deve ter no mínimo 2 caracteres.');
      return;
    }

    // Validação de descrição (mínimo 10 caracteres)
    if (formData.descricao.trim().length < 10) {
      setSubmitStatus('error');
      setErrorMessage('A descrição deve ter no mínimo 10 caracteres.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await apiService.sendContact({
        nome: formData.nome.trim(),
        email: formData.email.trim(),
        telefone: formData.telefone.trim(),
        tipoContato: 'corretagem',
        mensagem: `Tipo de Imóvel: ${formData.tipoImovel}\n\nDescrição:\n${formData.descricao.trim()}`
      });

      if (response.success) {
        setSubmitStatus('success');
        // Limpar formulário após sucesso
        setFormData({
          nome: '',
          email: '',
          telefone: '',
          tipoImovel: '',
          descricao: ''
        });
        // Fechar modal após 2 segundos
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setSubmitStatus('error');
        setErrorMessage(response.message || 'Erro ao enviar formulário. Por favor, tente novamente.');
      }
    } catch (error: any) {
      setSubmitStatus('error');
      // Tentar extrair mensagem de erro do backend
      let errorMsg = 'Erro ao enviar formulário. Por favor, tente novamente.';
      
      if (error?.message) {
        errorMsg = error.message;
      } else if (error?.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (typeof error === 'string') {
        errorMsg = error;
      }
      
      setErrorMessage(errorMsg);
      console.error('Erro ao enviar formulário de corretagem:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="corretagem-modal-overlay" onClick={handleOverlayClick}>
      <div className="corretagem-modal" onClick={(e) => e.stopPropagation()}>
        <div className="corretagem-modal-header">
          <h2>Corretagem</h2>
          <button 
            className="corretagem-modal-close"
            onClick={onClose}
            aria-label="Fechar modal"
          >
            ✕
          </button>
        </div>

        <div className="corretagem-modal-body">
          <p className="corretagem-modal-description">
            Nossos corretores entrarão em contato para conseguir o imóvel desejado.
          </p>

          <form onSubmit={handleSubmit} className="corretagem-form">
            <div className="corretagem-form-group">
              <label htmlFor="nome">Nome *</label>
              <input
                type="text"
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
                placeholder="Seu nome completo"
                required
                disabled={isSubmitting}
                className="corretagem-form-input"
              />
            </div>

            <div className="corretagem-form-group">
              <label htmlFor="email">E-mail *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="seu@email.com"
                required
                disabled={isSubmitting}
                className="corretagem-form-input"
              />
            </div>

            <div className="corretagem-form-group">
              <label htmlFor="telefone">Telefone *</label>
              <input
                type="tel"
                id="telefone"
                name="telefone"
                value={formData.telefone}
                onChange={handleInputChange}
                placeholder="(00) 00000-0000"
                required
                disabled={isSubmitting}
                className="corretagem-form-input"
              />
            </div>

            <div className="corretagem-form-group">
              <label htmlFor="tipoImovel">Tipo de Imóvel *</label>
              <select
                id="tipoImovel"
                name="tipoImovel"
                value={formData.tipoImovel}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
                className="corretagem-form-select"
              >
                <option value="">Selecione o tipo de imóvel</option>
                <option value={TipoImovel.APARTAMENTO}>Apartamento</option>
                <option value={TipoImovel.CASA}>Casa</option>
                <option value={TipoImovel.KITNET}>Kitnet</option>
                <option value={TipoImovel.STUDIO}>Studio</option>
                <option value={TipoImovel.LOFT}>Loft</option>
                <option value={TipoImovel.DUPLEX}>Duplex</option>
                <option value={TipoImovel.COBERTURA}>Cobertura</option>
                <option value={TipoImovel.TERRENO}>Terreno</option>
                <option value={TipoImovel.COMERCIAL}>Comercial</option>
                <option value={TipoImovel.GALPAO}>Galpão</option>
              </select>
            </div>

            <div className="corretagem-form-group">
              <label htmlFor="descricao">Descrição *</label>
              <textarea
                id="descricao"
                name="descricao"
                value={formData.descricao}
                onChange={handleInputChange}
                placeholder="Descreva o imóvel que você procura..."
                rows={4}
                required
                disabled={isSubmitting}
                className="corretagem-form-textarea"
              />
            </div>

            {submitStatus === 'success' && (
              <div className="corretagem-form-message corretagem-form-message-success">
                Formulário enviado com sucesso! Nossos corretores entrarão em contato em breve.
              </div>
            )}

            {submitStatus === 'error' && errorMessage && (
              <div className="corretagem-form-message corretagem-form-message-error">
                {errorMessage}
              </div>
            )}

            <div className="corretagem-form-actions">
              <button
                type="button"
                onClick={onClose}
                className="corretagem-btn corretagem-btn-cancel"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="corretagem-btn corretagem-btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

